import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { US_STATES, getCountiesForState } from '../data/us-locations';
import {
  fetchConsultRecommendations,
  uploadConsultPhoto,
  formatPriceTier,
  storePendingProductUrl,
  consumePendingProductUrl,
  openProductUrl,
  handlePatternImageError
} from '../utils/consultUtils';
import USStateHeatMap from '../components/maps/USStateHeatMap';
import './ConsultPage.css';

const ConsultPage = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [selectedState, setSelectedState] = useState('');
  const [selectedCounty, setSelectedCounty] = useState('');
  const [counties, setCounties] = useState([]);
  const [environmentPhoto, setEnvironmentPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [results, setResults] = useState(null);
  const [redirectPrompt, setRedirectPrompt] = useState(null);

  const [stateSearchTerm, setStateSearchTerm] = useState('');
  const [showStateDropdown, setShowStateDropdown] = useState(false);
  const [filteredStates, setFilteredStates] = useState([]);

  const [countySearchTerm, setCountySearchTerm] = useState('');
  const [showCountyDropdown, setShowCountyDropdown] = useState(false);
  const [filteredCounties, setFilteredCounties] = useState([]);

  useEffect(() => {
    const pendingUrl = consumePendingProductUrl();
    if (pendingUrl && currentUser) {
      setRedirectPrompt(pendingUrl);
    }
  }, [currentUser]);

  useEffect(() => {
    if (stateSearchTerm.trim()) {
      const term = stateSearchTerm.toLowerCase();
      setFilteredStates(
        US_STATES.filter(
          (s) =>
            s.name.toLowerCase().includes(term) ||
            s.code.toLowerCase().includes(term)
        )
      );
    } else {
      setFilteredStates([]);
    }
  }, [stateSearchTerm]);

  useEffect(() => {
    if (countySearchTerm.trim() && counties.length) {
      const term = countySearchTerm.toLowerCase();
      setFilteredCounties(
        counties.filter((c) => c.toLowerCase().includes(term))
      );
    } else {
      setFilteredCounties([]);
    }
  }, [countySearchTerm, counties]);

  const applyStateSelection = (stateCode, stateName) => {
    setSelectedState(stateCode);
    setStateSearchTerm(stateName);
    setShowStateDropdown(false);
    setSelectedCounty('');
    setCountySearchTerm('');
    setCounties(getCountiesForState(stateCode));
  };

  const handleStateDropdownSelect = (state) => {
    applyStateSelection(state.code, state.name);
  };

  const handleMapStateSelect = (stateCode, stateName) => {
    applyStateSelection(stateCode, stateName);
  };

  const handleCountySelect = (county) => {
    setSelectedCounty(county);
    setCountySearchTerm(county);
    setShowCountyDropdown(false);
  };

  const handlePhotoSelect = (e) => {
    if (!currentUser) return;
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      setEnvironmentPhoto(file);
      setPhotoPreview(URL.createObjectURL(file));
      setError('');
    } else {
      setError('Please select a valid image file.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!selectedState) {
      setError('Please select a state.');
      return;
    }

    setLoading(true);
    setResults(null);

    try {
      let photoUrl = null;
      let idToken = null;

      if (environmentPhoto && currentUser) {
        idToken = await currentUser.getIdToken(true);
        photoUrl = await uploadConsultPhoto(environmentPhoto, currentUser.uid);
      }

      const data = await fetchConsultRecommendations({
        state: selectedState,
        county: selectedCounty || null,
        photoUrl,
        userId: currentUser?.uid || null,
        idToken
      });

      setResults(data);
    } catch (err) {
      console.error('Consult error:', err);
      setError(err.message || 'Failed to get recommendations. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleProductClick = (productUrl) => {
    setRedirectPrompt(productUrl);
  };

  const handleRedirectCancel = () => {
    setRedirectPrompt(null);
  };

  const handleRedirectConfirm = () => {
    if (!redirectPrompt) return;

    const productUrl = redirectPrompt;
    setRedirectPrompt(null);

    if (currentUser) {
      openProductUrl(productUrl);
      return;
    }

    storePendingProductUrl(productUrl);
    const returnTo = encodeURIComponent('/consult');
    const redirect = encodeURIComponent(productUrl);
    navigate(`/login?returnTo=${returnTo}&redirect=${redirect}`);
  };

  const selectedStateName =
    US_STATES.find((s) => s.code === selectedState)?.name || selectedState;

  return (
    <div className="consult-page">
      <div className="page-header">
        <h1>Camo Consult</h1>
        <p>Tell us where you are headed and we will recommend patterns from our curated catalog.</p>
      </div>

      <form className="consult-form" onSubmit={handleSubmit}>
        <div className="consult-field consult-field--map">
          <USStateHeatMap
            selectedState={selectedState}
            onStateSelect={handleMapStateSelect}
            variant="select"
            showTooltip
            seamless
            className="us-state-heatmap--seamless us-state-heatmap--consult"
          />
        </div>

        <div className="consult-field">
          <label htmlFor="state-search">State *</label>
          <div className="consult-dropdown">
            <input
              id="state-search"
              type="text"
              value={stateSearchTerm}
              onChange={(e) => {
                setStateSearchTerm(e.target.value);
                setShowStateDropdown(true);
              }}
              onFocus={() => setShowStateDropdown(true)}
              placeholder="Search states..."
              autoComplete="off"
            />
            {showStateDropdown && filteredStates.length > 0 && (
              <ul className="consult-dropdown-list">
                {filteredStates.map((state) => (
                  <li key={state.code}>
                    <button
                      type="button"
                      onClick={() => handleStateDropdownSelect(state)}
                    >
                      {state.name} ({state.code})
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="consult-field">
          <label htmlFor="county-search">County (optional)</label>
          <div className="consult-dropdown">
            <input
              id="county-search"
              type="text"
              value={countySearchTerm}
              onChange={(e) => {
                setCountySearchTerm(e.target.value);
                setShowCountyDropdown(true);
              }}
              onFocus={() => selectedState && setShowCountyDropdown(true)}
              placeholder={selectedState ? 'Search counties...' : 'Select a state first'}
              disabled={!selectedState}
              autoComplete="off"
            />
            {showCountyDropdown && filteredCounties.length > 0 && (
              <ul className="consult-dropdown-list">
                {filteredCounties.map((county) => (
                  <li key={county}>
                    <button type="button" onClick={() => handleCountySelect(county)}>
                      {county}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="consult-field">
          <label>Environment photo (optional)</label>
          {currentUser ? (
            <div className="consult-photo-upload">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handlePhotoSelect}
                className="consult-file-input"
              />
              <button
                type="button"
                className="consult-photo-btn"
                onClick={() => fileInputRef.current?.click()}
              >
                {environmentPhoto ? 'Change photo' : 'Upload photo'}
              </button>
              {photoPreview && (
                <div className="consult-photo-preview vignette">
                  <img src={photoPreview} alt="Environment preview" />
                </div>
              )}
            </div>
          ) : (
            <p className="consult-photo-hint">
              <button
                type="button"
                className="consult-link-btn"
                onClick={() => navigate('/login?returnTo=%2Fconsult')}
              >
                Sign in
              </button>{' '}
              to upload a photo for more accurate recommendations.
            </p>
          )}
        </div>

        {error && <p className="consult-error">{error}</p>}

        <button type="submit" className="consult-submit-btn" disabled={loading}>
          {loading ? 'Searching...' : 'Search'}
        </button>
      </form>

      {results && (
        <section className="consult-results">
          <div className="consult-summary">
            <h2>
              Recommendations for{' '}
              {selectedCounty ? `${selectedCounty} County, ` : ''}
              {selectedStateName}
            </h2>
            <p>{results.environmentSummary}</p>
          </div>

          <div className="consult-grid">
            {results.recommendations.map((rec) => (
              <button
                key={rec.id}
                type="button"
                className={`consult-card${rec.isCultCamo ? ' consult-card--cult-camo' : ''}`}
                onClick={() => handleProductClick(rec.productUrl)}
              >
                <div className="consult-card-image vignette">
                  <img
                    src={rec.imageUrl}
                    alt={`${rec.brand} ${rec.patternName}`}
                    onError={handlePatternImageError}
                  />
                </div>
                <div className="consult-card-info">
                  <span className="consult-brand">{rec.brand}</span>
                  <h3>{rec.patternName}</h3>
                  <span className="consult-price-tier">{formatPriceTier(rec.priceTier)}</span>
                  <p className="consult-rationale">{rec.description || rec.rationale}</p>
                </div>
              </button>
            ))}
          </div>

          {redirectPrompt && (
            <div
              className="consult-redirect-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="consult-redirect-title"
              onClick={handleRedirectCancel}
            >
              <div
                className="consult-redirect-dialog"
                onClick={(e) => e.stopPropagation()}
              >
                <h3 id="consult-redirect-title">Continue to seller site?</h3>
                <p className="consult-redirect-message">
                  You are about to be redirected to:
                </p>
                <p className="consult-redirect-url">{redirectPrompt}</p>
                <p className="consult-redirect-message">Do you wish to proceed?</p>
                <div className="consult-redirect-actions">
                  <button
                    type="button"
                    className="consult-redirect-btn consult-redirect-btn--secondary"
                    onClick={handleRedirectCancel}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="consult-redirect-btn consult-redirect-btn--primary"
                    onClick={handleRedirectConfirm}
                  >
                    Proceed
                  </button>
                </div>
              </div>
            </div>
          )}

          <p className="consult-disclaimer">
            Camo Endemico may earn commission from purchases. Patterns are sold by third-party brands.
            {!currentUser && ' Sign in to visit seller links.'}
          </p>
        </section>
      )}
    </div>
  );
};

export default ConsultPage;
